// biome-ignore lint/correctness/noUnusedImports: existing error
import React, {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Spinner} from '~/common/components/Loader/Spinner';
import {
  selectDefaultValue,
  selectEGiftCardProducts,
  selectRetailer,
  TOGGLE_E_GIFT_CARDS_DRAWER,
  UPDATE_E_GIFT_CARD_RETAILER,
  UPDATE_E_GIFT_CARD_VALUE,
} from '~/common/fabric/GiftCard/reducers/eGiftCard';
import {getResponsiveSizeToHintScroll} from '~/common/hooks/useResponsivelySizeToHintScroll';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useBrowserHeight} from '~library/hooks/useBrowserHeight';
import './EGiftCardProducts.sass';

export const EGiftCardProducts = () => {
  const dispatch = useDispatch();
  const products = useSelector(selectEGiftCardProducts);
  const retailer = useSelector(selectRetailer);
  const defaultValue = useSelector(selectDefaultValue);
  const browserHeight = useBrowserHeight({timeout: 0});

  const numRowsVisible = useMemo(() => {
    if (browserHeight < 600) return 2;
    if (browserHeight < 800) return 3;
    if (browserHeight < 1000) return 4;
    return 5;
  }, [browserHeight]);

  const responsiveProductHeight = getResponsiveSizeToHintScroll({
    numVisible: numRowsVisible,
    // Full height, minus the header, bottom nav, top padding of the gift card controller,
    // title of the gift card controller, and the gap between the title and the gift card grid
    containerSize: `(100vh - 5rem - 4.25rem - 1.5rem - 1.375rem - 1rem)`,
    itemGap: 0.5,
    itemGapUnits: 'rem',
  });

  const onClickHandler = useCallback(
    (product) => {
      dispatch({type: UPDATE_E_GIFT_CARD_RETAILER, payload: product});
      dispatch({type: TOGGLE_E_GIFT_CARDS_DRAWER, payload: false});

      if (product?.productConfigId !== retailer?.productConfigId) {
        dispatch({type: UPDATE_E_GIFT_CARD_VALUE, payload: defaultValue});
      }
    },
    [retailer, defaultValue, dispatch]
  );

  const productsMemoized = useMemo(
    () =>
      products?.map((product, idx) => (
        <div
          className="retailer-product__wrapper"
          key={product?.product_config_id || `${product?.product_config_id}-${idx}`}
          style={{height: responsiveProductHeight}}
        >
          <Button
            data-qa-id="retailer-product"
            className={`retailer-product ${
              product?.product_config_id === retailer?.productConfigId ? 'selected' : ''
            }`}
            variant="transparent"
            onClick={() => onClickHandler(product)}
          >
            <img
              className="product"
              src={product?.image}
              alt={product?.product_config_name}
              aria-label={`${product?.product_config_name} image`}
            />

            <Typography variant="paragraph4" color="shadow">
              {product?.product_config_name}
            </Typography>
          </Button>
        </div>
      )),
    [products, onClickHandler, retailer?.productConfigId, responsiveProductHeight]
  );

  if (!products) {
    return <Spinner center />;
  }

  return <div className="gift-card-products">{productsMemoized}</div>;
};
